import { EnvGroup } from "@flakiness/server/common/stats/envGroup.js";
import { Stats } from "@flakiness/server/common/stats/stats.js";
import { WireTypes } from "@flakiness/server/common/wireTypes.js";
import { Task, TaskConfig, TaskStatus } from "@lit/task";
import { ReactiveControllerHost, ReactiveElement } from "lit";
import { api } from "./api.js";
import { CHECK } from '@flakiness/shared/common/utils.js';
import { PageMetadata, setPageMetadata } from './pageMetadata.js';


export class TimedTask<T extends ReadonlyArray<unknown> = ReadonlyArray<unknown>, R = unknown> extends Task<T, R> {
  private _start: number = 0;
  private _timeout?: NodeJS.Timeout;
  constructor(host: ReactiveElement, rerenderTimeout: number, task: TaskConfig<T, R>) {
    super(host, {
      ...task,
      task: async (...args) => {
        if (this._timeout)
          clearTimeout(this._timeout);

        this._timeout = setTimeout(() => host.requestUpdate(), rerenderTimeout);
        this._start = Date.now();

        const result = await task.task(...args);
        clearTimeout(this._timeout);
        this._timeout = undefined;
        return result;
      }
    });
  }

  duration() {
    return this.status === TaskStatus.PENDING ? Date.now() - this._start : 0;
  }
}


export type HEADCommitOptions = WireTypes.ListCommitOptions & {
  since?: Date,
  until?: Date,
}

export namespace tasks {
  // Every page declares its own document metadata. Binding the task to the
  // state a title depends on, such as the project from the project context,
  // applies the metadata as soon as that state lands and again whenever it
  // changes. Pages with static metadata pass no args.
  export function pageMetadata<T extends ReadonlyArray<unknown>>(host: ReactiveControllerHost, options: {
    args: () => T,
    metadata: (args: T) => PageMetadata|undefined,
  }) {
    return new Task(host, {
      args: options.args,
      task: async (args: T) => {
        const metadata = options.metadata(args);
        if (metadata)
          setPageMetadata(metadata);
      },
    });
  }

  export function headRef(host: ReactiveControllerHost, options: {
    head: () => string|undefined,
    project: () => WireTypes.Project|undefined,
    snapshotId: () => WireTypes.DataSnapshotId|undefined,
    defaultBranch: () => WireTypes.Ref|undefined,
  }) {
    return new Task(host, {
      args: () => [
        options.project(), options.snapshotId(), options.defaultBranch(), options.head(),
      ] as const,
      task: async ([project, snapshotId, defaultBranch, head], { signal }) => {
        if (!head || head === defaultBranch?.name)
          return defaultBranch;
        if (head) {
          CHECK(project && snapshotId);
          const response = await api.project.resolveRef.GET({
            orgSlug: project.org.orgSlug,
            projectSlug: project.projectSlug,
            snapshotId,
            head,
          }, { signal });
          CHECK(response);
          return response;
        }
        CHECK(defaultBranch);
        return defaultBranch;
      }
    });
  }

  export function billing(host: ReactiveControllerHost, options: {
    orgSlug: () => string|undefined,
  }) {
    return new Task(host, {
      args: () => [options.orgSlug()] as const,
      task: async ([orgSlug], { signal }) => {
        CHECK(orgSlug);
        return await api.billing.status.GET({ orgSlug }, { signal });
      }
    });
  }

  export function orgMembers(host: ReactiveControllerHost, options: {
    orgSlug: () => string|undefined,
  }) {
    return new Task(host, {
      args: () => [options.orgSlug()] as const,
      task: async ([orgSlug], { signal }) => {
        CHECK(orgSlug);
        const result = await api.organization.listMembers.GET({ orgSlug }, { signal });
        return result.members;
      }
    });
  }

  export function orgProjects(host: ReactiveControllerHost, options: {
    orgSlug: () => string|undefined,
  }) {
    return new Task(host, {
      args: () => [options.orgSlug()] as const,
      task: async ([orgSlug], { signal }) => {
        CHECK(orgSlug);
        return await api.organization.listProjects.GET({ orgSlug }, { signal }) ?? [];
      }
    });
  }

  export function allEnvironments(host: ReactiveControllerHost, options: {
    project: () => WireTypes.Project|undefined,
    snapshotId: () => WireTypes.DataSnapshotId|undefined,
    commitOptions: () => WireTypes.ListCommitOptions|undefined,
    testId?: () => Stats.TestId|undefined,
  }) {
    return new Task(host, {
        args: () => [
          options.project(), options.snapshotId(), options.commitOptions(), options.testId?.call(null)
        ] as const,
        argsEqual: ([
          project1, snapshotId1, commitOptions1, testId1,
        ], [
          project2, snapshotId2, commitOptions2, testId2,
        ]) => {
          return testId1 === testId2 && project1 === project2 && snapshotId1 === snapshotId2 && isEqualCommitOptions(commitOptions1, commitOptions2);
        },
        task: async ([project, snapshotId, commitOptions, testId], { signal }) => {
          CHECK(project && snapshotId && commitOptions);
          return await api.report.allEnvironments.GET({
            orgSlug: project.org.orgSlug,
            projectSlug: project.projectSlug,
            snapshotId,
            commitOptions,
            testId,
          }, { signal });
        }
      }
    );
  }

  /** Fetches the split that "All Environments" stands for, see `DataSnapshot.defaultEnvGroup`. */
  export function defaultSplit(host: ReactiveControllerHost, options: {
    project: () => WireTypes.Project|undefined,
    snapshotId: () => WireTypes.DataSnapshotId|undefined,
    preset: () => WireTypes.ReportPreset|undefined,
  }) {
    // A default equal to the last one keeps its identity, so consumers that compare splits by
    // identity do not refetch when a new snapshot or head leaves the default as it was.
    let lastGroup: EnvGroup|undefined;
    return new Task(host, {
      args: () => [
        options.project(), options.snapshotId(), options.preset(),
      ] as const,
      argsEqual: ([project1, snapshotId1, preset1], [project2, snapshotId2, preset2]) => {
        return project1 === project2 && snapshotId1 === snapshotId2 && JSON.stringify(preset1) === JSON.stringify(preset2);
      },
      task: async ([project, snapshotId, preset], { signal }) => {
        CHECK(project && snapshotId && preset);
        const json = await api.report.defaultSplit.GET({
          orgSlug: project.org.orgSlug,
          projectSlug: project.projectSlug,
          snapshotId,
          preset,
        }, { signal });
        const group = EnvGroup.deserialize(json);
        if (!lastGroup?.isEqual(group))
          lastGroup = group;
        return lastGroup;
      },
    });
  }
}

function isEqualCommitOptions(c1: WireTypes.ListCommitOptions|undefined, c2: WireTypes.ListCommitOptions|undefined): boolean {
  // Both undefined are equal
  if (!c1 && !c2)
    return true;
  // Either undefined is not equal
  if (!c1 || !c2)
    return false;
  return c1.head === c2.head &&
         c1.headOffset === c2.headOffset &&
         c1.maxCount === c2.maxCount &&
         c1.sinceTimestamp === c2.sinceTimestamp &&
         c1.untilTimestamp === c2.untilTimestamp;
}
